// QR Kiosk v2 — shared dashboard tile atom (kiosk-legibility rework 2026-07-23,
// KV2_DESIGN_REVIEW_FEEDBACK.md §1). Restores v1's uniform tile board virtues:
// colored left-accent bar, big colored glyph (+50%), heavy label, optional mono
// count badge. Consumed by DashboardTileGrid AND tasks/MyTasksRow (extracted to
// its own file so tasks → dashboard imports stay acyclic). Tokens only.
import React from "react";
import { Glyph } from "../shared/glyphs";
import { usePressed } from "../shared/usePressed";

export interface DashTileProps {
  glyph: string;
  label: string;
  /** CSS color expression for the accent bar + glyph (e.g. "var(--purple)"). */
  accent: string;
  /** Optional count rendered as a mono badge in the top-right corner. */
  count?: number;
  onClick: () => void;
}

export function DashTile({ glyph, label, accent, count, onClick }: DashTileProps) {
  const press = usePressed();
  // Text/glyph legibility: pull the accent toward ink the same way the v2 rows did.
  const accentInk = `color-mix(in oklab, ${accent} 78%, var(--ink))`;
  return (
    <button
      type="button"
      {...press.pressHandlers}
      onClick={onClick}
      style={{
        // L6: minHeight, not height — a 3-line localized label (DE "Offene Serviceberichte"
        // at 320px) grows the tile (and its grid row) instead of being clipped by overflow:hidden.
        minHeight: 112,
        width: "100%",
        background: "var(--card)",
        border: "1px solid var(--border)",
        borderRadius: 13,
        boxShadow: "var(--shadow)",
        padding: "14px 14px 14px 18px",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        textAlign: "left",
        fontFamily: "inherit",
        cursor: "pointer",
        ...press.pressStyle,
      }}
    >
      {/* v1-style colored left accent bar */}
      <span
        style={{
          position: "absolute",
          left: 0,
          top: 12,
          bottom: 12,
          width: 4,
          borderRadius: "0 4px 4px 0",
          background: accent,
        }}
      />
      <span style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", width: "100%" }}>
        <span style={{ color: accentInk, display: "inline-flex" }}>
          <Glyph d={glyph} size={32} />
        </span>
        {count != null && count > 0 && (
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 15,
              fontWeight: 700,
              minWidth: 28,
              height: 28,
              padding: "0 8px",
              borderRadius: 999,
              display: "inline-grid",
              placeItems: "center",
              background: `color-mix(in oklab, ${accent} 14%, var(--card))`,
              border: `1px solid color-mix(in oklab, ${accent} 38%, var(--border))`,
              color: accentInk,
            }}
          >
            {count}
          </span>
        )}
      </span>
      <span
        className="kv2-hy"
        style={{
          marginTop: "auto",
          fontSize: 15.5,
          fontWeight: 700,
          color: "var(--ink)",
          lineHeight: 1.25,
        }}
      >
        {label}
      </span>
    </button>
  );
}

export default DashTile;
